import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  createEstabelecimento,
  estabelecimentoKeys,
  fetchEstabelecimentos,
  updateEstabelecimento,
} from '@/services/estabelecimentoService';
import { fetchEstadosIBGE, fetchMunicipiosIBGE, ibgeKeys } from '@/services/ibgeService';
import { parseApiError } from '@/utils/apiError';
import { formatCNPJ, unformatCNPJ } from '@/utils/helpers';
import { Button } from '@/components/ui/button';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { estabelecimentoFormSchema, type TEstabelecimentoFormData } from './schema';
import type { IEstabelecimento, IEstadoIBGE, IMunicipioIBGE } from '@/types/estabelecimento';

export const Route = createFileRoute('/_authenticated/admin/estabelecimentos/')({
  component: Estabelecimentos,
});

const columns: Array<{ key: keyof IEstabelecimento; label: string }> = [
  { key: 'numero', label: 'Número' },
  { key: 'ambito', label: 'Âmbito' },
  { key: 'cpf_Cnpj', label: 'CPF/CNPJ' },
  { key: 'cod_uf', label: 'COD UF' },
  { key: 'nome_municipio_ibge', label: 'Nome Município IBGE' },
  { key: 'cod_municipio_ibge', label: 'COD Município IBGE' },
];

const AMBITO_OPTIONS = [
  { value: 'ER', label: 'ER' },
  { value: 'SIF', label: 'SIF' },
];

const emptyForm: TEstabelecimentoFormData = {
  numero: '',
  ambito: '',
  cpf_Cnpj: '',
  cod_uf: '',
  cod_municipio_ibge: '',
};

function formatCellValue(columnKey: keyof IEstabelecimento, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  if (columnKey === 'cpf_Cnpj') return formatCNPJ(String(value));
  return String(value);
}

function buildRowKey(item: IEstabelecimento): string {
  return `${item.numero}-${item.cod_uf}`;
}

function Estabelecimentos() {
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  const isEditing = Boolean(editingKey);

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TEstabelecimentoFormData>({
    resolver: zodResolver(estabelecimentoFormSchema),
    defaultValues: emptyForm,
  });
  const codUf = useWatch({ control, name: 'cod_uf' });

  const { data: items = [], isLoading, isError, error } = useQuery({
    queryKey: estabelecimentoKeys.list,
    queryFn: fetchEstabelecimentos,
  });

  const { data: estadosIBGE = [], isLoading: estadosLoading, isError: estadosError } = useQuery({
    queryKey: ibgeKeys.estados,
    queryFn: fetchEstadosIBGE,
  });

  const { data: municipiosIBGE = [], isLoading: municipiosLoading, isError: municipiosError } = useQuery({
    queryKey: ibgeKeys.municipios(codUf),
    queryFn: () => fetchMunicipiosIBGE(codUf),
    enabled: Boolean(codUf),
  });

  const createMutation = useMutation({ mutationFn: createEstabelecimento });
  const updateMutation = useMutation({
    mutationFn: ({ numero, codUf: uf, payload }: { numero: string; codUf: string; payload: Parameters<typeof updateEstabelecimento>[2] }) =>
      updateEstabelecimento(numero, uf, payload),
  });
  const saving = createMutation.isPending || updateMutation.isPending;

  const openCreate = () => {
    setEditingKey(null);
    reset(emptyForm);
    setShowForm(true);
  };

  const openEdit = (item: IEstabelecimento) => {
    setEditingKey(buildRowKey(item));
    reset({
      numero: item.numero || '',
      ambito: item.ambito || '',
      cpf_Cnpj: item.cpf_Cnpj || '',
      cod_uf: item.cod_uf || '',
      cod_municipio_ibge: item.cod_municipio_ibge ? String(item.cod_municipio_ibge) : '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingKey(null);
    reset(emptyForm);
  };

  const onSubmit = async (data: TEstabelecimentoFormData) => {
    try {
      const cpfCnpj = unformatCNPJ(data.cpf_Cnpj);
      const codMunicipioIbge = Number(data.cod_municipio_ibge);

      if (isEditing) {
        await updateMutation.mutateAsync({
          numero: data.numero,
          codUf: data.cod_uf,
          payload: { ambito: data.ambito, cpf_Cnpj: cpfCnpj, cod_municipio_ibge: codMunicipioIbge },
        });
      } else {
        await createMutation.mutateAsync({
          numero: data.numero,
          ambito: data.ambito,
          cpf_Cnpj: cpfCnpj,
          cod_uf: data.cod_uf,
          cod_municipio_ibge: codMunicipioIbge,
        });
      }

      closeForm();
      await queryClient.invalidateQueries({ queryKey: estabelecimentoKeys.all });
    } catch (err) {
      toast.error(parseApiError(err, isEditing ? 'Erro ao atualizar estabelecimento' : 'Erro ao criar estabelecimento'));
    }
  };

  const listErrorMessage = useMemo(() => (isError ? parseApiError(error, 'Erro ao listar estabelecimentos') : null), [isError, error]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold text-on-surface">Estabelecimentos</h1>
        <p className="text-on-surface-variant">Gerencie os estabelecimentos do sistema.</p>
      </div>

      <div className="rounded-lg border border-outline-variant">
        <div className="flex flex-col gap-4 border-b border-outline-variant p-4 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-semibold text-on-surface">Lista de Estabelecimentos</h2>
          <Button
            className="bg-success text-on-primary hover:bg-success/90"
            type="button"
            onClick={openCreate}
          >
            Adicionar estabelecimento
          </Button>
        </div>

        <div className="p-4">
          {listErrorMessage && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-4 rounded-lg bg-error-container p-4 text-on-error-container"
            >
              {listErrorMessage}
            </div>
          )}
          {isLoading && (
            <LoadingSpinner
              text="Carregando..."
              size="small"
            />
          )}
          {!isLoading && (
            <div className="block max-w-full overflow-x-auto overflow-y-hidden">
              <table className="w-max min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-outline-variant text-left">
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="whitespace-nowrap p-3"
                      >
                        {column.label}
                      </th>
                    ))}
                    <th className="w-24 whitespace-nowrap p-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={buildRowKey(item)}
                      className="border-b border-outline-variant last:border-0"
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className="whitespace-nowrap p-3"
                        >
                          {formatCellValue(column.key, item[column.key])}
                        </td>
                      ))}
                      <td className="whitespace-nowrap p-3">
                        <Button
                          variant="outline"
                          type="button"
                          onClick={() => openEdit(item)}
                          disabled={saving}
                          title="Editar"
                        >
                          Editar
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="p-6 text-center text-on-surface-variant"
                      >
                        Nenhum estabelecimento encontrado
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          if (!open) closeForm();
        }}
        modal={false}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Editar Estabelecimento' : 'Adicionar Estabelecimento'}</DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
          >
            <FieldGroup>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field data-invalid={!!errors.numero}>
                  <FieldLabel htmlFor="numero">Número</FieldLabel>
                  <Input
                    id="numero"
                    disabled={isEditing}
                    aria-invalid={!!errors.numero}
                    {...register('numero')}
                  />
                  <FieldError errors={errors.numero ? [errors.numero] : undefined} />
                </Field>

                <Field data-invalid={!!errors.ambito}>
                  <FieldLabel htmlFor="ambito">Âmbito</FieldLabel>
                  <Controller
                    control={control}
                    name="ambito"
                    render={({ field }) => (
                      <Combobox
                        items={AMBITO_OPTIONS}
                        itemToStringLabel={(option: (typeof AMBITO_OPTIONS)[number]) => option.label}
                        value={AMBITO_OPTIONS.find((option) => option.value === field.value) ?? null}
                        onValueChange={(option: (typeof AMBITO_OPTIONS)[number] | null) => field.onChange(option?.value ?? '')}
                      >
                        <ComboboxInput
                          id="ambito"
                          placeholder="Selecione o âmbito"
                          autoComplete="off"
                          aria-invalid={!!errors.ambito}
                        />
                        <ComboboxContent>
                          <ComboboxEmpty>Nenhuma opção encontrada</ComboboxEmpty>
                          <ComboboxList>
                            {(option: (typeof AMBITO_OPTIONS)[number]) => (
                              <ComboboxItem
                                key={option.value}
                                value={option}
                              >
                                {option.label}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  <FieldError errors={errors.ambito ? [errors.ambito] : undefined} />
                </Field>

                <Field data-invalid={!!errors.cpf_Cnpj}>
                  <FieldLabel htmlFor="cpf_Cnpj">CPF/CNPJ</FieldLabel>
                  <Controller
                    control={control}
                    name="cpf_Cnpj"
                    render={({ field }) => (
                      <Input
                        id="cpf_Cnpj"
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                        value={field.value}
                        onChange={(e) => field.onChange(formatCNPJ(e.target.value))}
                        onBlur={field.onBlur}
                        aria-invalid={!!errors.cpf_Cnpj}
                      />
                    )}
                  />
                  <FieldError errors={errors.cpf_Cnpj ? [errors.cpf_Cnpj] : undefined} />
                </Field>

                <Field data-invalid={!!errors.cod_uf}>
                  <FieldLabel htmlFor="cod_uf">COD UF</FieldLabel>
                  <Controller
                    control={control}
                    name="cod_uf"
                    render={({ field }) => (
                      <Combobox
                        items={estadosIBGE}
                        itemToStringLabel={(estado: IEstadoIBGE) => estado.sigla}
                        value={estadosIBGE.find((estado) => estado.sigla === field.value) ?? null}
                        onValueChange={(estado: IEstadoIBGE | null) => {
                          field.onChange(estado?.sigla ?? '');
                          setValue('cod_municipio_ibge', '');
                        }}
                        disabled={isEditing || estadosLoading || estadosError}
                      >
                        <ComboboxInput
                          id="cod_uf"
                          placeholder="Selecione uma UF"
                          autoComplete="off"
                          aria-invalid={!!errors.cod_uf}
                        />
                        <ComboboxContent>
                          <ComboboxEmpty>Nenhuma UF encontrada</ComboboxEmpty>
                          <ComboboxList>
                            {(estado: IEstadoIBGE) => (
                              <ComboboxItem
                                key={estado.sigla}
                                value={estado}
                              >
                                {estado.sigla}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  {estadosLoading && <FieldDescription>Carregando UFs...</FieldDescription>}
                  {estadosError && (
                    <FieldDescription
                      role="alert"
                      className="text-destructive"
                    >
                      Erro ao carregar UFs
                    </FieldDescription>
                  )}
                  <FieldError errors={errors.cod_uf ? [errors.cod_uf] : undefined} />
                </Field>

                <Field data-invalid={!!errors.cod_municipio_ibge}>
                  <FieldLabel htmlFor="cod_municipio_ibge">COD Município IBGE</FieldLabel>
                  <Controller
                    control={control}
                    name="cod_municipio_ibge"
                    render={({ field }) => (
                      <Combobox
                        items={municipiosIBGE}
                        itemToStringLabel={(municipio: IMunicipioIBGE) => municipio.nome}
                        value={municipiosIBGE.find((municipio) => String(municipio.id) === field.value) ?? null}
                        onValueChange={(municipio: IMunicipioIBGE | null) => field.onChange(municipio ? String(municipio.id) : '')}
                        disabled={!codUf || municipiosLoading || municipiosError}
                      >
                        <ComboboxInput
                          id="cod_municipio_ibge"
                          placeholder={codUf ? 'Selecione um município' : 'Selecione uma UF primeiro'}
                          autoComplete="off"
                          aria-invalid={!!errors.cod_municipio_ibge}
                        />
                        <ComboboxContent>
                          <ComboboxEmpty>Nenhum município encontrado</ComboboxEmpty>
                          <ComboboxList>
                            {(municipio: IMunicipioIBGE) => (
                              <ComboboxItem
                                key={municipio.id}
                                value={municipio}
                              >
                                {municipio.nome}
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  />
                  {municipiosLoading && <FieldDescription>Carregando municípios...</FieldDescription>}
                  {municipiosError && (
                    <FieldDescription
                      role="alert"
                      className="text-destructive"
                    >
                      Erro ao carregar municípios
                    </FieldDescription>
                  )}
                  <FieldError errors={errors.cod_municipio_ibge ? [errors.cod_municipio_ibge] : undefined} />
                </Field>
              </div>

              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancelar
                </Button>
              </div>
            </FieldGroup>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
